import type { Region } from '@backoffice-frontend/graphql'

export type SelectedRegion = Pick<Region, 'id' | 'name' | 'operatorTenant'> & {
  timeZoneId?: Region['timeZoneId']
  defaultLanguage?: Region['defaultLanguage']
}

// Apollo resolves GET_SELECTED_REGION to nothing when the region lacks a field
// the query selects, so every stored region carries all of them.
export type CompleteSelectedRegion = Pick<
  SelectedRegion,
  'id' | 'name' | 'operatorTenant'
> & {
  timeZoneId: Region['timeZoneId'] | null
  defaultLanguage: Region['defaultLanguage'] | null
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

const stringOrNull = (value: unknown) =>
  typeof value === 'string' ? value : null

// Stored regions are older than the fields added to SelectedRegion since, or
// were written by callers that only knew some of them.
export const parsePersistedRegion = (
  value: unknown,
): CompleteSelectedRegion | undefined => {
  if (!isRecord(value)) return undefined

  const { id, name, operatorTenant } = value
  if (
    typeof id !== 'string' ||
    typeof name !== 'string' ||
    typeof operatorTenant !== 'string'
  ) {
    return undefined
  }

  return {
    id,
    name,
    operatorTenant,
    timeZoneId: stringOrNull(value.timeZoneId),
    defaultLanguage: stringOrNull(value.defaultLanguage),
  }
}

// Keeps the fields a caller leaves out from the stored copy of the same region.
export const mergeSelectedRegion = (
  current: CompleteSelectedRegion | undefined,
  next: SelectedRegion,
): CompleteSelectedRegion => {
  const sameRegion = current?.id === next.id ? current : undefined

  return {
    id: next.id,
    name: next.name,
    operatorTenant: next.operatorTenant,
    timeZoneId: next.timeZoneId ?? sameRegion?.timeZoneId ?? null,
    defaultLanguage:
      next.defaultLanguage ?? sameRegion?.defaultLanguage ?? null,
  }
}

export const toSelectedRegion = ({
  timeZoneId,
  defaultLanguage,
  ...region
}: CompleteSelectedRegion): SelectedRegion => ({
  ...region,
  timeZoneId: timeZoneId ?? undefined,
  defaultLanguage: defaultLanguage ?? undefined,
})

export const isSameSelectedRegion = (
  a: CompleteSelectedRegion | undefined,
  b: CompleteSelectedRegion | undefined,
) =>
  a === b ||
  (!!a &&
    !!b &&
    a.id === b.id &&
    a.name === b.name &&
    a.operatorTenant === b.operatorTenant &&
    a.timeZoneId === b.timeZoneId &&
    a.defaultLanguage === b.defaultLanguage)
